import { message, Spin } from 'antd';
import React, { useEffect } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { Link, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import DangerButton from '../../shared/components/buttons/DangerButton';
import PrimaryButton from '../../shared/components/buttons/PrimaryButton';
import CancelIcon from '../../shared/svgs/CancelIcon';
import DeleteForeverIcon from '../../shared/svgs/DeleteForeverIcon';
import EditIcon from '../../shared/svgs/EditIcon';
import { doDeleteCustomer, useGetCustomerDetails } from '../../api/customers';
import { CurrentUserI } from '../../interfaces/auth';
import { getUsername, handleAccessControl } from '../../utils/global';
import BackButton from '../../shared/components/buttons/BackButton';
import CustomerLicensesTable from '../../shared/components/tables/CustomerLicenses';
import dayjs from 'dayjs';
import { DATE_FORMAT_FRONTEND } from '../../shared/constants';

const CustomerDetailsPage: React.FC = () => {
  const currentUser: CurrentUserI = useOutletContext();
  const access = handleAccessControl(currentUser.roles, 'customers');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  const { data: customer, isLoading, refetch: fetchCustomer } = useGetCustomerDetails(Number(id));

  const { mutateAsync: mutationDelete } = useMutation(doDeleteCustomer, {
    onSuccess: async () => {
      message.info('Deleted!');
      queryClient.invalidateQueries({
        queryKey: ['customers', 'list'],
      });
      queryClient.invalidateQueries({
        queryKey: [`customers/${id}`, undefined],
      });
      navigate(`/customers`, { replace: true });
    },
  });

  const handleDelete = async () => {
    mutationDelete(Number(id));
  };

  useEffect(() => {
    fetchCustomer();
  }, []);

  return (
    <Spin spinning={isLoading}>
      <div id="company-details-container">
        <div className="page-container">
          <div className="page-content">
            <div className="page-content-header">
              <h2 className="title-page">Customer Details</h2>
            </div>
            <div className="details-container">
              <div className="item">
                <label>Name</label>
                <span className="value">{customer?.name}</span>
              </div>
              <div className="item">
                <label>Created At</label>
                <span className="value">{dayjs(customer?.createdAt).format(DATE_FORMAT_FRONTEND)}</span>
              </div>
              <div className="item">
                <label>Created By</label>
                <span className="value">{customer?.createdBy ? getUsername(customer?.createdBy) : '-'}</span>
              </div>
              <div className="item">
                <label>Updated At</label>
                <span className="value">
                  {customer?.updatedBy ? dayjs(customer?.updatedAt).format(DATE_FORMAT_FRONTEND) : '-'}
                </span>
              </div>
              <div className="item">
                <label>Updated By</label>
                <span className="value">{customer?.updatedBy ? getUsername(customer?.updatedBy) : '-'}</span>
              </div>
              <div className="item">
                <label>Remark</label>
                <span className="value">{customer?.remark}</span>
              </div>
            </div>
            <div className="page-content-footer">
              {access.isCanUpdate && (
                <Link to={`/customers/${id}/edit`}>
                  <PrimaryButton label="Edit" icon={<EditIcon />} />
                </Link>
              )}
              {access.isCanDelete && <DangerButton label="Delete" onOk={handleDelete} icon={<DeleteForeverIcon />} />}
              <BackButton label="Cancel" icon={<CancelIcon />} />
            </div>
          </div>
          {customer?.licenses && (
            <div className="page-content">
              <div className="page-content-header">
                <h2 className="title-page">Licenses/Films</h2>
              </div>
              <CustomerLicensesTable
                rowKey="id"
                loading={isLoading}
                pagination={false}
                dataSource={customer?.licenses}
              />
            </div>
          )}
        </div>
      </div>
    </Spin>
  );
};

export default CustomerDetailsPage;
